// src/pages/Hierarchy/KandaPage.js
import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser, isParokiaAdmin as checkParokiaAdmin } from "../../services/auth";
import {
  getJimbos,
  getParokia,     // supports ?jimboId=...
  getMyParokia,   // /parokia/self  (keep for display fallback)
  getKanda,       // ?parokiaId=...
  createKanda,    // POST /kanda
} from "../../services/api";
import JumuiaModal from "./JumuiaModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./hierarchy.css";

export default function KandaPage() {
  const user = getCurrentUser();
  const isParokiaAdmin = checkParokiaAdmin();

  /* =========================
     FILTERS / LOOKUPS
  ========================= */
  const [jimbos, setJimbos] = useState([]);
  const [jimbosLoading, setJimbosLoading] = useState(false);

  const [selectedJimbo, setSelectedJimbo] = useState("");
  const [parokiaList, setParokiaList] = useState([]);
  const [parokiaLoading, setParokiaLoading] = useState(false);

  const [selectedParokia, setSelectedParokia] = useState("");

  /* =========================
     KANDA LIST
  ========================= */
  const [kanda, setKanda] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ NEW: Context for Parokia Admin derived from Kanda list (or fallback)
  const [context, setContext] = useState({
    jimbo_name: "",
    parokia_name: "",
    jimbo_id: null,
    parokia_id: null,
  });

  /* =========================
     CREATE MODAL + FORM
  ========================= */
  const [createOpen, setCreateOpen] = useState(false);

  const initialForm = {
    name: "",
    mwenyekiti: "",
    katibu: "",
    mweka_hazina: "",
    status: "ACTIVE",
  };

  const [form, setForm] = useState(initialForm);

  /* =========================
     JUMUIA MODAL (existing)
  ========================= */
  const [selectedKanda, setSelectedKanda] = useState(null);
  const [jumuia, setJumuia] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const openJumuia = async (k) => {
    setSelectedKanda(k);
    setModalOpen(true);
    setModalLoading(true);

    try {
      const api = await import("../../services/api");
      const res = await api.getJumuia(k.parokia_id, k.kanda_id);
      setJumuia(res.data || []);
    } catch (err) {
      console.error("Failed to load Jumuia", err);
      setJumuia([]);
      toast.error("Failed to load Jumuia");
    } finally {
      setModalLoading(false);
    }
  };

  /* =========================
     LOADERS
  ========================= */

  useEffect(() => {
    if (!isParokiaAdmin) {
      // SYSTEM ADMIN
      loadJimbos();
    } else {
      // PAROKIA ADMIN: lock parokiaId from token
      const myId = user?.parokiaId;
      if (!myId) {
        toast.error("No parokia assigned to your account");
        return;
      }
      setSelectedParokia(String(myId));
      loadMyParokia();     // for display fallback only
      loadKanda(myId);     // ✅ will also set context from API result
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJimbos = async () => {
    setJimbosLoading(true);
    try {
      const res = await getJimbos();
      setJimbos(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Failed to load Jimbos", err);
      toast.error("Failed to load Jimbos");
      setJimbos([]);
    } finally {
      setJimbosLoading(false);
    }
  };

  // Parokia Admin only - fallback for names
  const loadMyParokia = async () => {
    try {
      const res = await getMyParokia();
      const list = res.data || [];

      if (!Array.isArray(list) || list.length === 0) {
        // fallback from token
        setParokiaList([
          {
            parokia_id: user?.parokiaId,
            parokia_name: user?.parokiaName,
          },
        ]);
        // also set fallback context
        setContext((prev) => ({
          ...prev,
          parokia_id: user?.parokiaId ?? prev.parokia_id,
          parokia_name: user?.parokiaName ?? prev.parokia_name,
        }));
        return;
      }

      setParokiaList(list);

      // if API provides parokia name/jimbo name, you could optionally set it here,
      // but we primarily set context from Kanda list (more reliable for your UI request).
    } catch (err) {
      console.error("Failed to load my parokia", err);
      setParokiaList([
        {
          parokia_id: user?.parokiaId,
          parokia_name: user?.parokiaName,
        },
      ]);
      setContext((prev) => ({
        ...prev,
        parokia_id: user?.parokiaId ?? prev.parokia_id,
        parokia_name: user?.parokiaName ?? prev.parokia_name,
      }));
    }
  };

  // System Admin: load parokia by jimbo
  const loadParokiaByJimbo = async (jimboId) => {
    if (!jimboId) {
      setParokiaList([]);
      setSelectedParokia("");
      setKanda([]);
      return;
    }

    setParokiaLoading(true);
    try {
      const res = await getParokia(jimboId);
      const list = res.data || [];
      setParokiaList(Array.isArray(list) ? list : []);
      setSelectedParokia("");
      setKanda([]);
    } catch (err) {
      console.error("Failed to load parokia by jimbo", err);
      toast.error("Failed to load Parokia list");
      setParokiaList([]);
      setSelectedParokia("");
      setKanda([]);
    } finally {
      setParokiaLoading(false);
    }
  };

  const loadKanda = async (parokiaId) => {
    const effectiveParokiaId = isParokiaAdmin ? user?.parokiaId : parokiaId;

    if (!effectiveParokiaId) {
      setKanda([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getKanda(effectiveParokiaId);
      const list = res.data || [];
      setKanda(list);

      // ✅ IMPORTANT: For PAROKIA ADMIN, set Jimbo/Parokia context from Kanda API response
      if (isParokiaAdmin && Array.isArray(list) && list.length > 0) {
        const first = list[0];
        setContext({
          jimbo_name: first.jimbo_name || "",
          parokia_name: first.parokia_name || user?.parokiaName || "",
          jimbo_id: first.jimbo_id ?? null,
          parokia_id: first.parokia_id ?? user?.parokiaId ?? null,
        });
      } else if (isParokiaAdmin) {
        // if no kanda yet, keep at least parokia name from token
        setContext((prev) => ({
          ...prev,
          parokia_id: user?.parokiaId ?? prev.parokia_id,
          parokia_name: user?.parokiaName ?? prev.parokia_name,
        }));
      }
    } catch (err) {
      console.error("Failed to load kanda", err);
      toast.error(err?.response?.data?.message || "Failed to load Kanda");
      setKanda([]);
    } finally {
      setLoading(false);
    }
  };

  // System admin: whenever parokia changes, load kanda
  useEffect(() => {
    if (!isParokiaAdmin && selectedParokia) {
      loadKanda(selectedParokia);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParokia, isParokiaAdmin]);

  /* =========================
     CREATE KANDA (PAROKIA ADMIN ONLY)
  ========================= */
  const handleCreateKanda = async () => {
    if (!isParokiaAdmin) {
      toast.error("Only Parokia Admin can create Kanda");
      return;
    }

    const myParokiaId = user?.parokiaId;

    if (!myParokiaId) {
      toast.error("No parokia assigned");
      return;
    }

    if (!form.name.trim()) {
      toast.error("Kanda name is required");
      return;
    }

    if (!window.confirm("Create this Kanda?")) return;

    try {
      await createKanda({
        parokiaId: myParokiaId,
        name: form.name.trim(),
        mwenyekiti: form.mwenyekiti || null,
        katibu: form.katibu || null,
        mweka_hazina: form.mweka_hazina || null,
        status: form.status || "ACTIVE",
      });

      toast.success("Kanda created successfully");
      setCreateOpen(false);
      setForm(initialForm);
      await loadKanda(myParokiaId);
    } catch (err) {
      console.error("Failed to create Kanda", err);
      toast.error(err?.response?.data?.message || "Failed to create Kanda");
    }
  };

  /* =========================
     UI HELPERS
  ========================= */

  // ✅ Keep System Admin behavior as before (uses parokiaList selection)
  const selectedParokiaObj = useMemo(() => {
    if (!selectedParokia) return null;
    return parokiaList.find((p) => String(p.parokia_id) === String(selectedParokia)) || null;
  }, [parokiaList, selectedParokia]);

  // ✅ Unified context used in UI:
  // - System Admin: from selectedParokiaObj
  // - Parokia Admin: from API-derived "context"
  const viewContext = useMemo(() => {
    if (!isParokiaAdmin) {
      return {
        jimbo_name: selectedParokiaObj?.jimbo_name || "",
        parokia_name: selectedParokiaObj?.parokia_name || "",
      };
    }
    return {
      jimbo_name: context.jimbo_name || "",
      parokia_name: context.parokia_name || user?.parokiaName || "",
    };
  }, [isParokiaAdmin, selectedParokiaObj, context, user]);

  const closeCreateModal = () => {
    setCreateOpen(false);
    setForm(initialForm);
  };

  // small helper for enterprise labels
  const FieldLabel = ({ children }) => (
    <label style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13 }}>
      {children}
    </label>
  );

  return (
    <div className="page-container">
      <div className="hierarchy-header">
        <div>
          <h1>Kanda Management</h1>
          <p className="subtitle">
            {isParokiaAdmin
              ? "Manage Kanda under your Parokia"
              : "View Kanda by selecting Jimbo and Parokia"}
          </p>
        </div>

        {isParokiaAdmin && (
          <button className="btn primary" onClick={() => setCreateOpen(true)}>
            + New Kanda
          </button>
        )}
      </div>

      {/* FILTER CARD */}
      <div className="card">
        {!isParokiaAdmin ? (
          <div className="form-row jimbo">
            <div>
              <FieldLabel>Jimbo</FieldLabel>
              <select
                className="input"
                value={selectedJimbo}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedJimbo(id);
                  loadParokiaByJimbo(id);
                }}
                disabled={jimbosLoading}
              >
                <option value="">
                  {jimbosLoading ? "Loading Jimbos..." : "Select Jimbo"}
                </option>
                {jimbos.map((j) => (
                  <option key={j.jimbo_id} value={j.jimbo_id}>
                    {j.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel>Parokia</FieldLabel>
              <select
                className="input"
                value={selectedParokia}
                onChange={(e) => setSelectedParokia(e.target.value)}
                disabled={!selectedJimbo || parokiaLoading}
              >
                <option value="">
                  {parokiaLoading ? "Loading Parokia..." : "Select Parokia"}
                </option>
                {parokiaList.map((p) => (
                  <option key={p.parokia_id} value={p.parokia_id}>
                    {p.parokia_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span className="subtitle" style={{ margin: 0 }}>
                {selectedParokiaObj?.jimbo_name ? (
                  <>
                    <strong>Jimbo:</strong> {selectedParokiaObj.jimbo_name}{" "}
                    &nbsp;|&nbsp; <strong>Parokia:</strong> {selectedParokiaObj.parokia_name}
                  </>
                ) : (
                  "Select Jimbo and Parokia to view Kanda"
                )}
              </span>
            </div>
          </div>
        ) : (
          <div className="form-row jimbo">
            <div>
              <FieldLabel>Jimbo</FieldLabel>
              <input
                className="input"
                value={viewContext.jimbo_name || "—"}
                readOnly
                placeholder="Jimbo"
              />
            </div>

            <div>
              <FieldLabel>Parokia</FieldLabel>
              <input
                className="input"
                value={viewContext.parokia_name || "—"}
                readOnly
                placeholder="Parokia"
              />
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span className="subtitle" style={{ margin: 0 }}>
                You can create Kanda only in your Parokia.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* TABLE CARD */}
      <div className="card">
        <div className="card-header between">
          <div>
            <h3 style={{ margin: 0 }}>Available Kanda</h3>
            <p className="subtitle" style={{ marginTop: 6 }}>
              {viewContext.parokia_name
                ? `Showing Kanda for ${viewContext.parokia_name}`
                : isParokiaAdmin
                ? "Loading your Kanda..."
                : "Select Jimbo and Parokia to view Kanda"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="spinner" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Kanda</th>
                <th>Parokia</th>
                <th>Jimbo</th>
                <th>Mwenyekiti</th>
                <th>Katibu</th>
                <th>Mweka Hazina</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {!selectedParokia ? (
                <tr>
                  <td colSpan="9" className="empty">
                    {isParokiaAdmin ? "No parokia assigned" : "Please select Jimbo and Parokia"}
                  </td>
                </tr>
              ) : kanda.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty">
                    No Kanda found
                  </td>
                </tr>
              ) : (
                kanda.map((k, i) => (
                  <tr key={k.kanda_id}>
                    <td>{i + 1}</td>
                    <td className="bold">{k.kanda_name || k.name}</td>
                    <td>{k.parokia_name || viewContext.parokia_name || "-"}</td>
                    <td>{k.jimbo_name || viewContext.jimbo_name || "-"}</td>
                    <td>{k.mwenyekiti || "-"}</td>
                    <td>{k.katibu || "-"}</td>
                    <td>{k.mweka_hazina || "-"}</td>
                    <td>
                      <span className="badge-success">{k.status}</span>
                    </td>
                    <td>
                      <button className="btn small" onClick={() => openJumuia(k)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE MODAL (PAROKIA ADMIN ONLY) */}
      {createOpen && isParokiaAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "95%", maxWidth: "900px" }}>
            <div className="modal-close" onClick={closeCreateModal}>
              ✕
            </div>

            <h3>Create Kanda</h3>

            <div className="modal-section">
              <h4>Context</h4>
              <div className="form-grid">
                <div>
                  <FieldLabel>Jimbo</FieldLabel>
                  <input
                    className="input"
                    readOnly
                    value={viewContext.jimbo_name || "—"}
                    placeholder="Jimbo"
                  />
                </div>

                <div>
                  <FieldLabel>Parokia</FieldLabel>
                  <input
                    className="input"
                    readOnly
                    value={viewContext.parokia_name || "—"}
                    placeholder="Parokia"
                  />
                </div>
              </div>
            </div>

            <div className="modal-section">
              <h4>Basic Information</h4>
              <div className="form-grid">
                <div>
                  <FieldLabel>Kanda Name</FieldLabel>
                  <input
                    className="input"
                    placeholder="Enter Kanda name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-section">
              <h4>Leadership (Kamati Tendaji)</h4>
              <div className="form-grid">
                <div>
                  <FieldLabel>Mwenyekiti</FieldLabel>
                  <input
                    className="input"
                    placeholder="Enter Mwenyekiti"
                    value={form.mwenyekiti}
                    onChange={(e) => setForm({ ...form, mwenyekiti: e.target.value })}
                  />
                </div>

                <div>
                  <FieldLabel>Katibu</FieldLabel>
                  <input
                    className="input"
                    placeholder="Enter Katibu"
                    value={form.katibu}
                    onChange={(e) => setForm({ ...form, katibu: e.target.value })}
                  />
                </div>

                <div>
                  <FieldLabel>Mweka Hazina</FieldLabel>
                  <input
                    className="input"
                    placeholder="Enter Mweka Hazina"
                    value={form.mweka_hazina}
                    onChange={(e) => setForm({ ...form, mweka_hazina: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn" onClick={closeCreateModal}>
                Cancel
              </button>
              <button className="btn primary" onClick={handleCreateKanda}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JUMUIA MODAL */}
      {modalOpen && (
        <JumuiaModal
          kanda={selectedKanda}
          jumuia={jumuia}
          loading={modalLoading}
          onClose={() => setModalOpen(false)}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}